/**
 * The clock a sandbox test run runs on — installed, run under, and given back — in ONE place for every browser-side
 * runner: `BrowserRunnerAdapter`, the app's in-page test runner (`SandboxRunnerProvider`) and `SandboxRunner`. The node
 * runner interleaves its own install with the page it builds (NodeRunnerAdapter.ts) and restores the same way.
 */
import { installDeterministicGlobals, restoreGlobals } from './testRuntime';
import { installVirtualClock, restoreVirtualClock } from './virtualClock';

/**
 * Which clock a run gets: `'virtual'` — the advance-able virtual clock a `defineSandboxTest` game runs on (its booter
 * ticks it); `'deterministic'` — the seeded `Math.random` and frozen `Date.now` of a run with no game.
 */
export type SandboxRunClock = 'virtual' | 'deterministic';

/**
 * Runs `run` on `clock`, seeded with `seed`, and restores the clock when `run` settles — fulfilled or rejected. The
 * restore names THIS install: a run that outlived its test (a later run installed its own clock meanwhile) gives
 * nothing back, so the later run keeps its clock (`restoreVirtualClock` / `restoreGlobals` refuse a superseded token).
 * Resolves or rejects as `run` does.
 */
export async function runOnClock<T>(
  clock: SandboxRunClock,
  seed: { random: number; clockStart: number },
  run: () => Promise<T>,
): Promise<T> {
  if (clock === 'virtual') {
    const token = installVirtualClock(seed);
    try {
      return await run();
    } finally {
      restoreVirtualClock(token);
    }
  }
  const token = installDeterministicGlobals(seed);
  try {
    return await run();
  } finally {
    restoreGlobals(token);
  }
}
